import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, RefreshCw, Search, ArrowLeft, User, Plus, Pencil, Save, X } from 'lucide-react';
import { adminApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const ROLE_COLORS = {
  admin: '#FF6B35',
  editor: '#FFE800',
  user: '#39FF14',
};

const USERS_CACHE_PREFIX = 'wowrolla_admin_users_cache_v1';

const readCache = (key) => {
  try {
    const cached = JSON.parse(localStorage.getItem(key) || 'null');
    return cached && typeof cached === 'object' ? cached : null;
  } catch {
    return null;
  }
};

const writeCache = (key, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignore storage failures
  }
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const AdminUsersPage = () => {
  const { user: authUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [form, setForm] = useState({
    email: '',
    username: '',
    full_name: '',
    role: 'user',
    role_profile: '',
    company_size: '',
    newsletter_subscribed: false,
    is_active: true,
    password: '',
  });
  const [newType, setNewType] = useState({ key: '', label: '', description: '' });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    const cacheKey = `${USERS_CACHE_PREFIX}:${roleFilter || 'all'}`;
    const cached = readCache(cacheKey);
    if (cached?.users) {
      setUsers(Array.isArray(cached.users) ? cached.users : []);
      setLoading(false);
    }
    try {
      const res = await adminApi.listUsers(roleFilter ? { role: roleFilter } : {});
      const nextUsers = Array.isArray(res.data) ? res.data : [];
      setUsers(nextUsers);
      writeCache(cacheKey, { users: nextUsers, updatedAt: new Date().toISOString() });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  const loadUserTypes = useCallback(async () => {
    try {
      const res = await adminApi.listUserTypes();
      setUserTypes(Array.isArray(res.data) ? res.data : []);
    } catch {
      setUserTypes([
        { key: 'admin', label: 'Admin', active: true },
        { key: 'editor', label: 'Editor', active: true },
        { key: 'user', label: 'User', active: true },
      ]);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadUserTypes(); }, [loadUserTypes]);

  const activeRoleOptions = useMemo(
    () => userTypes.filter((t) => t.active).map((t) => ({ value: t.key, label: t.label || t.key })),
    [userTypes]
  );

  const resetForm = useCallback(() => {
    setEditingUserId(null);
    setForm({
      email: '',
      username: '',
      full_name: '',
      role: 'user',
      role_profile: '',
      company_size: '',
      newsletter_subscribed: false,
      is_active: true,
      password: '',
    });
  }, []);

  const openCreate = () => {
    resetForm();
    setEditorOpen(true);
  };

  const openEdit = (u) => {
    setEditingUserId(u.id || u.email);
    setForm({
      email: u.email || '',
      username: u.username || '',
      full_name: u.full_name || '',
      role: u.role || 'user',
      role_profile: u.role_profile || '',
      company_size: u.company_size || '',
      newsletter_subscribed: Boolean(u.newsletter_subscribed),
      is_active: u.is_active !== false,
      password: '',
    });
    setEditorOpen(true);
  };

  const handleSaveUser = async () => {
    setSaving(true);
    setError('');
    try {
      if (!editingUserId) {
        if (!form.email || !form.password) {
          throw new Error('Email and password are required for new users.');
        }
        await adminApi.createUser(form);
      } else {
        const payload = {
          username: form.username,
          full_name: form.full_name,
          role: form.role,
          role_profile: form.role_profile,
          company_size: form.company_size,
          newsletter_subscribed: form.newsletter_subscribed,
          is_active: form.is_active,
        };
        if (form.password?.trim()) payload.password = form.password;
        await adminApi.updateUser(editingUserId, payload);
      }
      setEditorOpen(false);
      resetForm();
      await loadUsers();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Permanently delete user ${email}? This cannot be undone.`)) return;
    setDeletingId(userId);
    setError('');
    try {
      await adminApi.deleteUser(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId && u.email !== userId));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateType = async () => {
    setError('');
    try {
      await adminApi.upsertUserType(newType);
      setNewType({ key: '', label: '', description: '' });
      await loadUserTypes();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save user type.');
    }
  };

  const handleDisableType = async (key) => {
    setError('');
    try {
      await adminApi.disableUserType(key);
      await loadUserTypes();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to disable user type.');
    }
  };

  const filtered = users.filter((u) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (u.email || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[#070707] text-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin" className="flex items-center gap-1 text-[#888] text-sm hover:text-white">
            <ArrowLeft size={14} /> Admin
          </Link>
          <span className="text-[#333]">/</span>
          <h1 className="font-unbounded text-lg font-bold text-white">Registered Users</h1>
          <span className="ml-auto font-mono text-[10px] px-2 py-1 border border-[#1c1c1c] text-[#888]">
            {filtered.length} users
          </span>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-[#FFE800] text-[#FFE800] hover:bg-[rgba(255,232,0,0.08)] transition-colors"
          >
            <Plus size={12} /> New User
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 border border-[#FF6B35] bg-[rgba(255,107,53,0.08)] text-[#FF6B35] text-sm font-mono">
            {error}
          </div>
        )}

        {/* Controls */}
        <div className="flex gap-3 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email or username…"
              className="w-full bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm pl-9 pr-3 py-2 rounded-none focus:border-[#FFE800] outline-none"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none focus:border-[#FFE800] outline-none"
          >
            <option value="">All roles</option>
            {activeRoleOptions.map((role) => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>
          <button
            onClick={loadUsers}
            disabled={loading}
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[#1c1c1c] text-[#888] hover:border-[#FFE800] hover:text-[#FFE800] disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Table */}
        <div className="border border-[#1c1c1c] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1c1c1c] bg-[#0d0d0d]">
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#555]">User</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#555]">Email</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#555]">Role</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#555]">Auth</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#555]">Created</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#555]">Newsletter</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#555] font-mono text-xs">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#555] font-mono text-xs">No users found.</td></tr>
              )}
              {!loading && filtered.map((u) => (
                <tr key={u.id || u.email} className="border-b border-[#111] hover:bg-[#0d0d0d] transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                        style={{ background: ROLE_COLORS[u.role] || '#333', color: '#000' }}
                      >
                        {(u.username || u.email || 'U')[0].toUpperCase()}
                      </div>
                      <span className="text-white font-mono text-xs">{u.username || '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#aaa] text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className="font-mono text-[9px] px-2 py-0.5 border"
                      style={{ color: ROLE_COLORS[u.role] || '#888', borderColor: ROLE_COLORS[u.role] || '#333' }}
                    >
                      {u.role || 'user'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#666] text-xs font-mono">{u.auth_provider || 'password'}</td>
                  <td className="px-4 py-3 text-[#666] text-xs">{fmt(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[9px] ${u.newsletter_subscribed ? 'text-[#39FF14]' : 'text-[#444]'}`}>
                      {u.newsletter_subscribed ? '✓ subscribed' : 'no'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => openEdit(u)}
                        className="text-[#FFE800] hover:opacity-70 transition-opacity"
                        title={`Edit ${u.email}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(u.id || u.email, u.email)}
                        disabled={deletingId === (u.id || u.email) || (u.role || '').toLowerCase() === 'admin' || (u.email || '').toLowerCase() === (authUser?.email || '').toLowerCase()}
                        className="text-[#FF6B35] hover:opacity-70 disabled:opacity-30 transition-opacity"
                        title={(u.role || '').toLowerCase() === 'admin' ? 'Admin users are protected' : `Delete ${u.email}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 border border-[#1c1c1c] p-4 bg-[#0a0a0a]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-unbounded text-sm text-white">User Type Editor</h2>
            <span className="text-[#666] text-xs font-mono">Admin-manageable role catalog</span>
          </div>
          <div className="grid md:grid-cols-3 gap-3 mb-3">
            <input value={newType.key} onChange={(e) => setNewType((p) => ({ ...p, key: e.target.value }))} placeholder="role key (e.g. account_manager)" className="bg-[#111] border border-[#1c1c1c] text-white text-sm px-3 py-2" />
            <input value={newType.label} onChange={(e) => setNewType((p) => ({ ...p, label: e.target.value }))} placeholder="label" className="bg-[#111] border border-[#1c1c1c] text-white text-sm px-3 py-2" />
            <input value={newType.description} onChange={(e) => setNewType((p) => ({ ...p, description: e.target.value }))} placeholder="description" className="bg-[#111] border border-[#1c1c1c] text-white text-sm px-3 py-2" />
          </div>
          <button type="button" onClick={handleCreateType} className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-[#FFE800] text-[#FFE800] hover:bg-[rgba(255,232,0,0.08)] transition-colors">
            <Save size={12} /> Save User Type
          </button>
          <div className="mt-4 grid md:grid-cols-2 gap-2">
            {userTypes.map((type) => (
              <div key={type.key} className="border border-[#1c1c1c] px-3 py-2 flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">{type.label || type.key}</p>
                  <p className="text-[10px] font-mono text-[#666]">{type.key}{type.description ? ` - ${type.description}` : ''}</p>
                </div>
                {type.active ? (
                  !['admin', 'editor', 'user'].includes(type.key) && (
                    <button type="button" onClick={() => handleDisableType(type.key)} className="text-[10px] font-mono text-[#FF6B35] border border-[#FF6B35] px-2 py-1">Disable</button>
                  )
                ) : (
                  <span className="text-[10px] font-mono text-[#555]">Disabled</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {editorOpen && (
          <div className="fixed inset-0 z-50 bg-[rgba(0,0,0,0.72)] flex items-center justify-center p-4">
            <div className="w-full max-w-2xl border border-[#1c1c1c] bg-[#0a0a0a] p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-unbounded text-sm text-white">{editingUserId ? 'Edit User' : 'Create User'}</h3>
                <button type="button" onClick={() => { setEditorOpen(false); resetForm(); }} className="text-[#777] hover:text-white"><X size={16} /></button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <input disabled={Boolean(editingUserId)} value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="Email" className="bg-[#111] border border-[#1c1c1c] text-white text-sm px-3 py-2 disabled:opacity-50" />
                <input value={form.username} onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))} placeholder="Username" className="bg-[#111] border border-[#1c1c1c] text-white text-sm px-3 py-2" />
                <input value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} placeholder="Full name" className="bg-[#111] border border-[#1c1c1c] text-white text-sm px-3 py-2" />
                <select value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))} className="bg-[#111] border border-[#1c1c1c] text-white text-sm px-3 py-2">
                  {activeRoleOptions.map((role) => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
                <input value={form.role_profile} onChange={(e) => setForm((p) => ({ ...p, role_profile: e.target.value }))} placeholder="Role profile" className="bg-[#111] border border-[#1c1c1c] text-white text-sm px-3 py-2" />
                <input value={form.company_size} onChange={(e) => setForm((p) => ({ ...p, company_size: e.target.value }))} placeholder="Company size" className="bg-[#111] border border-[#1c1c1c] text-white text-sm px-3 py-2" />
                <input type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} placeholder={editingUserId ? 'New password (optional)' : 'Password'} className="bg-[#111] border border-[#1c1c1c] text-white text-sm px-3 py-2" />
                <label className="flex items-center gap-2 text-xs text-[#aaa]">
                  <input type="checkbox" checked={form.newsletter_subscribed} onChange={(e) => setForm((p) => ({ ...p, newsletter_subscribed: e.target.checked }))} /> Newsletter subscribed
                </label>
                <label className="flex items-center gap-2 text-xs text-[#aaa]">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} /> Account active
                </label>
              </div>
              <div className="mt-5 flex gap-3 justify-end">
                <button type="button" onClick={() => { setEditorOpen(false); resetForm(); }} className="px-4 py-2 border border-[#333] text-[#aaa] text-sm">Cancel</button>
                <button type="button" disabled={saving} onClick={handleSaveUser} className="px-4 py-2 border border-[#FFE800] text-[#FFE800] text-sm inline-flex items-center gap-2 disabled:opacity-50">
                  <Save size={13} /> {saving ? 'Saving...' : 'Save User'}
                </button>
              </div>
            </div>
          </div>
        )}

        <p className="mt-4 text-[#444] font-mono text-[10px]">
          Admin accounts are protected from deletion. Use Edit to update role, status, or profile safely.
        </p>
      </div>
    </div>
  );
};

export default AdminUsersPage;
